import { useCallback } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { useNavigate } from 'react-router';

import {
  DecisionPollingStatus,
  useDecisionPolling,
} from './useDecisionPolling';
import { useGetSharedFunctionality } from '../../../api/lending/lapiHooks';
import { useAccountNavigation } from '../../../hooks/useAccountNavigation';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { wait } from '../../../utils/Wait';
import { buildQueryString } from '../../utils/queryParams';

export function usePayLaterDecisionFlow() {
  const { stateKey } = useStateKey();
  const { navigateToAccount } = useAccountNavigation();
  const navigate = useNavigate();
  const { status, start } = useDecisionPolling();

  const { functionality } = useGetSharedFunctionality({
    functionalities: [
      'should_show_buyer_spending_limit_page',
      'iwocapay_skip_buyer_dashboard_redirect',
    ],
  });

  const isEnabled = Boolean(
    functionality?.iwocapay_skip_buyer_dashboard_redirect,
  );
  const shouldShowSpendingLimitPage = Boolean(
    functionality?.should_show_buyer_spending_limit_page,
  );

  const trigger = useCallback(
    async ({
      payLinkAmount,
      queryParams,
    }: {
      payLinkAmount: number;
      queryParams: Record<string, string>;
    }) => {
      if (!stateKey) return;

      const outcome = await start({ stateKey, payLinkAmount });

      trackOutcome(outcome);

      const queryString = buildQueryString(queryParams);

      // Allow modal to update state.
      await wait(1_000);

      switch (outcome) {
        case 'approved':
          navigate(
            shouldShowSpendingLimitPage
              ? `/pay/approved/${queryString}`
              : `/pay/choose-product/${queryString}`,
          );
          return;
        case 'partial':
        case 'declined':
        case 'blocked':
        case 'timeout':
        case 'error':
          navigateToAccount(queryString);
          return;
        default:
          return;
      }
    },
    [stateKey, start, navigate, navigateToAccount, shouldShowSpendingLimitPage],
  );

  return { isEnabled, status, trigger };
}

function trackOutcome(outcome: DecisionPollingStatus) {
  switch (outcome) {
    case 'approved':
      tracking.ampli.decisionModalViewed({
        decision_modal_state: 'approved',
      });
      return;
    case 'declined':
      tracking.ampli.decisionModalViewed({
        decision_modal_state: 'declined',
      });
      return;
    case 'blocked':
    case 'partial':
    case 'timeout':
    case 'error':
      tracking.ampli.decisionModalViewed({
        decision_modal_state: 'error',
      });
      return;
    default:
      return;
  }
}
